// Login Page Component
// =====================

import React, { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';

import Button from '../ui/Button';
import Input from '../ui/Input';
import { LyraLogo, ContentFrame } from '../ui';

const LoginPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || t('login.defaultError'));
      }
    } catch (err) {
      setError(tErrors('auth.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark px-4">
      <div className="w-full max-w-md">
        {/* Logo and title - Feature #155: SVG logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <LyraLogo size="lg" showText={true} />
          </div>
          <p className="text-neutral-medium">{tCommon('tagline')}</p>
        </div>

        {/* Login form card - Feature #288: Updated with ContentFrame and new design */}
        <ContentFrame
          variant="border"
          padding="lg"
          showAccent={true}
        >
          {/* Feature #280: Serif display font for headings */}
          <h2 className="text-2xl font-display font-semibold text-neutral-dark dark:text-white mb-6 text-center">
            {t('login.title')}
          </h2>

          {error && (
            <div
              className="mb-4 p-3 bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800 rounded-lg"
              role="alert"
              aria-live="assertive"
            >
              <p className="text-accent-600 dark:text-accent-400 text-sm text-center font-sans">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-sans font-medium text-neutral-dark dark:text-white mb-1">
                {t('login.email')}
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-sans font-medium text-neutral-dark dark:text-white mb-1">
                {t('login.password')}
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
                autoComplete="current-password"
              />
            </div>

            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="text-sm font-sans text-primary-600 hover:text-primary-700 transition-colors"
              >
                {t('login.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-neutral-medium text-sm font-sans">
              {t('login.noAccount')}{' '}
              <Link
                to="/register"
                className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                {t('login.createAccount')}
              </Link>
            </p>
          </div>
        </ContentFrame>

        {/* Development mode indicator */}
        <div className="mt-4 text-center">
          <p className="text-xs text-neutral-medium">
            {tCommon('devMode')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
